import styles from "./TrainModel.module.scss";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { App, Badge, Button, Progress } from "antd";
import { DownOutlined, LoadingOutlined } from "@ant-design/icons";
import classNames from "classnames";
import pluralize from "pluralize";
import { Dataset } from "../../../../types";
import { MLTaskStatus } from "../../../../vl-api/vlSchemas";
import { StarIcon } from "../../../../assets/icons/StarIcon";
import VLButton from "../../../uikit/Buttons/VLButton";
import VLPopover from "../../../uikit/VLPopover";
import { useVisibility } from "../../../../hooks/useVisibility";
import { useFeatureFlagService } from "../../../../hooks/useFeatureFlagService";
import { useTrainingTasks } from "./useTrainingTasks";
import { useValidationTasks } from "./useValidationTasks";
import { useSeenModelJobsStore } from "./useSeenModelJobsStore";
import { ModelJobDialogs } from "./ModelJobDialogs";
import { ModelJobsPanel } from "./ModelJobsPanel";
import {
  getErrorText,
  getFailedTasksPath,
  getModelCatalogPath,
  isActiveTask,
  KIND_TEXT,
  ModelJob,
  parseUtcDate,
  RESULT_ACTION,
  toModelJob,
  toPercent,
} from "./modelJobsUtils";

// Only jobs that finished recently get a "finished" notice on page load
const NOTICE_WINDOW_MS = 24 * 60 * 60 * 1000;
const EMPTY_IDS: string[] = [];
const NOTIFIABLE_STATUSES: MLTaskStatus[] = ["COMPLETED", "FAILED"];

const getRunningLabel = (runningJobs: ModelJob[]) => {
  if (runningJobs.length > 1) {
    return `${runningJobs.length.toLocaleString()} ${pluralize("job", runningJobs.length)} running`;
  }
  const [job] = runningJobs;
  return job.status === "INIT"
    ? `Starting ${KIND_TEXT[job.kind].toLowerCase()}`
    : `${KIND_TEXT[job.kind]} ${toPercent(job.progress)}%`;
};

const capitalize = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1);

const ONE_JOB_AT_A_TIME = "Available when the current job ends.";

export const TrainModel = ({
  dataset,
  isTrainWorthy,
  canManageJobs,
}: {
  dataset: Dataset;
  isTrainWorthy: boolean;
  canManageJobs: boolean;
}) => {
  const datasetId = dataset.id;
  const navigate = useNavigate();
  const { notification } = App.useApp();
  const trainModelBehavior = useFeatureFlagService("TRAIN_MODEL");
  const dialogs = useVisibility();
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const {
    tasks: trainingTasks,
    isLoading: isLoadingTraining,
    handleCancelTaskClick: handleCancelTrainingClick,
  } = useTrainingTasks(datasetId);
  const {
    tasks: validationTasks,
    isLoading: isLoadingValidation,
    handleCancelTaskClick: handleCancelValidationClick,
  } = useValidationTasks(datasetId);

  const seenIds = useSeenModelJobsStore(
    (state) => state.seenByDataset[datasetId] ?? EMPTY_IDS,
  );
  const notifiedIds = useSeenModelJobsStore(
    (state) => state.notifiedByDataset[datasetId] ?? EMPTY_IDS,
  );
  const markSeen = useSeenModelJobsStore((state) => state.markSeen);
  const markNotified = useSeenModelJobsStore((state) => state.markNotified);

  const { runningJobs, finishedJobs } = useMemo(() => {
    const all = [
      ...trainingTasks.map(toModelJob("TRAIN")),
      ...validationTasks.map(toModelJob("VALIDATE")),
    ].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    return {
      runningJobs: all.filter(isActiveTask),
      finishedJobs: all.filter((job) => !isActiveTask(job)),
    };
  }, [trainingTasks, validationTasks]);

  const unseenJobs = finishedJobs.filter(
    (job) => job.status !== "CANCELLED" && !seenIds.includes(job.id),
  );
  const hasUnseenFailure = unseenJobs.some((job) => job.status === "FAILED");
  const isRunning = runningJobs.length > 0;

  // A running job also greys out the TRAIN_MODEL flag, so check it first
  const newJobBlockedReason = isRunning
    ? ONE_JOB_AT_A_TIME
    : !trainModelBehavior.enabled
      ? capitalize(
          trainModelBehavior.reason ||
            "Model jobs are not available right now.",
        )
      : null;

  // With no job history the panel would only say "No model jobs yet", so the
  // first click goes straight to the job dialog. The panel still opens when a
  // job can't be started (it explains why) or while the job list is loading.
  const shouldOpenDialogDirectly =
    canManageJobs &&
    !newJobBlockedReason &&
    !isLoadingTraining &&
    !isLoadingValidation &&
    runningJobs.length === 0 &&
    finishedJobs.length === 0;

  const handlePanelOpenChange = (open: boolean) => {
    if (open && shouldOpenDialogDirectly) {
      dialogs.open();
      return;
    }
    setIsPanelOpen(open);
    if (!open && unseenJobs.length) {
      markSeen(
        datasetId,
        finishedJobs.map((job) => job.id),
      );
    }
  };

  const closePanel = () => handlePanelOpenChange(false);

  const handleViewModel = (job: ModelJob) => {
    markSeen(datasetId, [job.id]);
    closePanel();
    navigate(getModelCatalogPath(job.kind, datasetId));
  };

  const handleViewError = (job: ModelJob) => {
    markSeen(datasetId, [job.id]);
    closePanel();
    navigate(getFailedTasksPath(job.kind, datasetId));
  };

  const handleAbort = (job: ModelJob) => {
    if (job.kind === "TRAIN") {
      handleCancelTrainingClick(job.id);
    } else {
      handleCancelValidationClick(job.id);
    }
  };

  const handleNewJob = () => {
    closePanel();
    dialogs.open();
  };

  // One notice per job that finished (while here or while away), shown once
  useEffect(() => {
    if (isLoadingTraining || isLoadingValidation) {
      return;
    }
    // Only completed and failed jobs get a notice; running, starting and
    // user-aborted jobs never do
    const freshJobs = finishedJobs.filter(
      (job) =>
        NOTIFIABLE_STATUSES.includes(job.status) &&
        !notifiedIds.includes(job.id) &&
        !seenIds.includes(job.id) &&
        Date.now() - parseUtcDate(job.updatedAt).getTime() < NOTICE_WINDOW_MS,
    );
    if (!freshJobs.length) {
      return;
    }
    markNotified(
      datasetId,
      freshJobs.map((job) => job.id),
    );
    freshJobs.forEach((job) => {
      if (job.status === "COMPLETED") {
        notification.success({
          key: job.id,
          message: `${KIND_TEXT[job.kind]} completed`,
          description: job.modelName || undefined,
          btn: (
            <Button
              type="primary"
              size="small"
              onClick={() => {
                notification.destroy(job.id);
                markSeen(datasetId, [job.id]);
                navigate(getModelCatalogPath(job.kind, datasetId));
              }}
            >
              {RESULT_ACTION[job.kind].label}
            </Button>
          ),
        });
      } else if (job.status === "FAILED") {
        notification.error({
          key: job.id,
          message: `${KIND_TEXT[job.kind]} failed`,
          description: getErrorText(job.error) || job.modelName || undefined,
          btn: (
            <Button
              size="small"
              onClick={() => {
                notification.destroy(job.id);
                markSeen(datasetId, [job.id]);
                navigate(getFailedTasksPath(job.kind, datasetId));
              }}
            >
              View error
            </Button>
          ),
        });
      }
    });
  }, [
    finishedJobs,
    isLoadingTraining,
    isLoadingValidation,
    notifiedIds,
    seenIds,
    datasetId,
    markNotified,
    markSeen,
    navigate,
    notification,
  ]);

  // Menu-button affordance: a caret plus ARIA state, shown whenever the click
  // opens the panel (not when it jumps straight to the job dialog)
  const opensPanel = !shouldOpenDialogDirectly;
  const menuButtonProps = opensPanel
    ? { "aria-haspopup": "dialog" as const, "aria-expanded": isPanelOpen }
    : {};
  const renderLabel = (text: string) => (
    <span className={styles.buttonLabel}>
      {text}
      {opensPanel && (
        <DownOutlined
          aria-hidden
          className={classNames(styles.caret, {
            [styles.caretOpen]: isPanelOpen,
          })}
        />
      )}
    </span>
  );

  const renderButton = () => {
    if (isRunning) {
      const [job] = runningJobs;
      const icon =
        runningJobs.length === 1 && job.status === "IN_PROGRESS" ? (
          <Progress
            type="circle"
            size={16}
            percent={toPercent(job.progress)}
            showInfo={false}
            strokeWidth={14}
            className={styles.progressRing}
          />
        ) : (
          <LoadingOutlined />
        );
      return (
        <VLButton
          type="default"
          size="middle"
          className={classNames(styles.modelJobsButton, styles.running)}
          icon={icon}
          aria-label="Model Jobs"
          {...menuButtonProps}
        >
          {renderLabel(getRunningLabel(runningJobs))}
        </VLButton>
      );
    }
    return (
      <Badge
        dot={unseenJobs.length > 0}
        offset={[-4, 4]}
        className={classNames(styles.badge, {
          [styles.failed]: hasUnseenFailure,
        })}
      >
        <VLButton
          type="primary"
          isGradient
          size="middle"
          className={styles.modelJobsButton}
          icon={<StarIcon />}
          aria-label="Model Jobs"
          {...menuButtonProps}
        >
          {renderLabel("Model Jobs")}
        </VLButton>
      </Badge>
    );
  };

  return (
    <div className={styles.TrainModel}>
      <VLPopover
        arrow={false}
        placement="bottomRight"
        trigger="click"
        open={isPanelOpen}
        onOpenChange={handlePanelOpenChange}
        overlayClassName={styles.popover}
        content={
          <ModelJobsPanel
            runningJobs={runningJobs}
            finishedJobs={finishedJobs}
            seenIds={seenIds}
            canManageJobs={canManageJobs}
            newJobBlockedReason={newJobBlockedReason}
            onNewJob={handleNewJob}
            onAbort={handleAbort}
            onViewModel={handleViewModel}
            onViewError={handleViewError}
          />
        }
      >
        {renderButton()}
      </VLPopover>
      {canManageJobs && (
        <ModelJobDialogs
          isOpen={dialogs.isOpen}
          close={dialogs.close}
          isTrainWorthy={isTrainWorthy}
          dataset={dataset}
        />
      )}
    </div>
  );
};
